/**
 * Renders a branded social-share PNG (Instagram story / square post) with
 * the ambassador's referral QR code baked in. Pure canvas — no deps.
 */
const PURPLE = '#7A0B7A';
const INK = '#0B0B0E';

export const SOCIAL_FORMATS = {
  story: { w: 1080, h: 1920, label: 'Instagram / Facebook story' },
  square: { w: 1080, h: 1080, label: 'Square post' },
};

function wrapText(ctx, text, maxWidth) {
  const words = text.split(' ');
  const lines = [];
  let line = '';
  words.forEach((word) => {
    const test = line ? `${line} ${word}` : word;
    if (ctx.measureText(test).width > maxWidth && line) {
      lines.push(line);
      line = word;
    } else {
      line = test;
    }
  });
  if (line) lines.push(line);
  return lines;
}

function roundRect(ctx, x, y, w, h, r) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

export function renderSocialImage({ format, qrCanvas, ambassadorName, shareUrl, commissionLine }) {
  const { w, h } = SOCIAL_FORMATS[format];
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d');

  ctx.fillStyle = INK;
  ctx.fillRect(0, 0, w, h);
  ctx.fillStyle = PURPLE;
  ctx.fillRect(0, 0, w, 18);

  const story = format === 'story';
  const pad = 90;
  const headSize = story ? 92 : 68;
  const subSize = story ? 40 : 32;
  let y = story ? 300 : 110;

  ctx.fillStyle = PURPLE;
  ctx.font = `bold ${story ? 34 : 28}px Helvetica, Arial, sans-serif`;
  ctx.letterSpacing = '6px';
  ctx.fillText('OX RESIN · AMBASSADOR', pad, y);
  ctx.letterSpacing = '0px';
  y += story ? 90 : 70;

  ctx.fillStyle = '#ffffff';
  ctx.font = `bold ${headSize}px Helvetica, Arial, sans-serif`;
  wrapText(ctx, 'Get trade rates on every order.', w - pad * 2).forEach((line) => {
    ctx.fillText(line, pad, y);
    y += headSize * 1.12;
  });
  y += 10;

  ctx.fillStyle = 'rgba(255,255,255,0.75)';
  ctx.font = `${subSize}px Helvetica, Arial, sans-serif`;
  wrapText(ctx, `Sign up through ${ambassadorName}'s link — ProBound resin, ProEpoxy floors, ProDecor walling.`, w - pad * 2).forEach((line) => {
    ctx.fillText(line, pad, y);
    y += subSize * 1.35;
  });

  // Footer block (URL + strapline) is anchored to the bottom; the QR card
  // takes whatever room is left between the copy and the footer.
  const footerH = commissionLine ? 200 : 140;
  const footerTop = h - footerH - (story ? 140 : 60);
  const cardPad = 40;
  const avail = footerTop - (y + 40) - cardPad * 2;
  const qrSize = Math.min(story ? 560 : 400, avail);
  const cardW = qrSize + cardPad * 2;
  const cardX = (w - cardW) / 2;
  const cardY = y + 40 + Math.max(0, (avail - qrSize) / 2);
  ctx.fillStyle = '#ffffff';
  roundRect(ctx, cardX, cardY, cardW, qrSize + cardPad * 2, 32);
  ctx.fill();
  if (qrCanvas) ctx.drawImage(qrCanvas, cardX + cardPad, cardY + cardPad, qrSize, qrSize);

  let ty = footerTop + 40;
  ctx.textAlign = 'center';
  ctx.fillStyle = PURPLE;
  ctx.font = 'bold 30px Helvetica, Arial, sans-serif';
  ctx.fillText('SCAN OR VISIT', w / 2, ty);
  ty += 62;
  ctx.fillStyle = '#ffffff';
  const urlText = shareUrl.replace(/^https?:\/\//, '');
  let urlSize = 44;
  ctx.font = `bold ${urlSize}px Helvetica, Arial, sans-serif`;
  while (ctx.measureText(urlText).width > w - pad * 2 && urlSize > 18) {
    urlSize -= 2;
    ctx.font = `bold ${urlSize}px Helvetica, Arial, sans-serif`;
  }
  ctx.fillText(urlText, w / 2, ty);
  if (commissionLine) {
    ty += 60;
    ctx.fillStyle = 'rgba(255,255,255,0.6)';
    ctx.font = '30px Helvetica, Arial, sans-serif';
    ctx.fillText(commissionLine, w / 2, ty);
  }
  ctx.textAlign = 'left';

  return canvas;
}

export function downloadCanvas(canvas, filename) {
  const link = document.createElement('a');
  link.download = filename;
  link.href = canvas.toDataURL('image/png');
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}
